import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { roles } from '../../utils/roles';
import { LocalizationProvider, DesktopDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { abortController } from '../../utils/abortController';
import { BiReset } from 'react-icons/bi';
import withAuth from '../../lib/withAuth';
import useUser from '../../hooks/useUser';
import useSessionExpire from '../../hooks/useSessionExpire';
import Button from '@mui/material/Button';
import Backdrop from '@mui/material/Backdrop';
import Grid from '@mui/material/Grid';
import ButtonGroup from '@mui/material/ButtonGroup';
import Loader from '../../components/Loader';
import Alert from '../../components/Alert';
import TextField from '@mui/material/TextField';
import dayjs from 'dayjs';
import axios from 'axios';
import { Modal } from 'react-bootstrap';
import MaterialReactDataTable from '../../components/MaterialReactDataTable';
import { getPermissions } from '../../lib/accessControl';

const DocumentRequests = ({ logOut = () => {} }) => 
{
  const [isLoading, setIsLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");
  const location = useLocation();
  const { pathname } = useLocation();

  /* SESSION EXPIRY VARIABLES */
  const triggerSessionExpire = useSessionExpire();

  /* AUTHENTICATION VARIABLES */
  const currentUser = useUser();

  /* AUTHORIZATION VARAIBLES */
  const { isWritable = false } = getPermissions(currentUser?.role, pathname);
  const isDocumentTeamRole = currentUser?.role === roles[2];
  const isLCTeamRole = currentUser?.role === roles[3];
  const isBusinessUnitRole = [roles[4], roles[5], roles[10], roles[11], roles[13], roles[14], roles[16], roles[17]].includes(currentUser?.role);

  /* DATE VARIABLES */
  const [startDate, setStartDate] = useState(dayjs().startOf("month").format("MM/DD/YYYY"));
  const [endDate, setEndDate] = useState(dayjs().format("MM/DD/YYYY"));

  /* CONDITIONAL FILTER VARIABLES */
  const [isFilterable, setIsFilterable] = useState(true);
  const [filter, setFilter] = useState(true);
  const [isFiltered, setIsFiltered] = useState(true);
  
  /* DATATABLE VARIABLES */
  const [periodLockRequestsData, setPeriodLockRequestsData] = useState([]);
  const [isDataChanged, setIsDataChanged] = useState(false);
  const [toggleDisplayData, setToggleDisplayData] = useState(false);
  const tableRef = useRef();

  /* DATATABLE ROW VARIABLES */
  const [rowData, setRowData] = useState({});

  /* DATATABLE GLOBAL FILTER VARIABLES */
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  
  /* MODAL VARIABLES */
  const [showLockStatusModal, setShowLockStatusModal] = useState(false);
  const [reviewedComment, setReviewedComment] = useState("");

  useEffect(() => 
  {
    if (startDate || endDate)
    {
      setIsFilterable(true);
    }
    else
    {
      setIsFilterable(false);
    }

  }, [startDate, endDate]);

  useEffect(() => 
  {
    const fetchData = async () => 
    {
      setIsLoading(true);
      setShowAlert(false);
      setAlertMessage("");
      setAlertType("");

      // Determine the API URL based on user role
      let apiUrl = "/api/GetDocumentRequestsForExecutives";
      if (isDocumentTeamRole) {
        apiUrl = "/api/GetDocumentRequestsForDocumentTeam";
      } else if (isLCTeamRole) {
        apiUrl = "/api/GetDocumentRequestsForLCTeam";
      } else if (isBusinessUnitRole) {
        apiUrl = "/api/GetDocumentRequestsForBusinessUnits";
      }

      await axios({
        method: "post",
        url: apiUrl,
        data: {
          start_date: startDate,
          end_date: endDate,
          filter_value: globalFilterValue
        },
        signal: abortController.signal
      })
      .then((response) => 
      {
        setIsLoading(false);
        const { status, data } = response;

        if (status === 200) 
        {
          const { document_request_data: periodLockRequestsData = []} = data || {};

          if (filter && periodLockRequestsData.length === 0)
          {
            setAlertMessage("No data found that matches the selected filter options.");
            setAlertType("info");
            setShowAlert(true);
          }

          setPeriodLockRequestsData(periodLockRequestsData);
        }
        else 
        {
          setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
          setAlertType("error");
          setShowAlert(true);
        }
      })
      .catch((error) => 
      {
        console.error("Get All PeriodLockRequests Data Api: ", error);
        setIsLoading(false);

        if (axios.isCancel(error) || error.code === "ERR_CANCELED") 
        {
          return;
        }

        const status = error?.response?.status;

        if (status === 403) 
        {
          triggerSessionExpire();
        }
        else 
        {
          setAlertMessage(
            status === 401
              ? "Unauthorized access. You do not have the required permissions to perform this action."
              : status === 429
              ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
              : "An error occurred while processing your request. Please try again later or contact the site administrator."
          );
          setAlertType("error");
          setShowAlert(true);

          if (status === 429) 
          {
            setTimeout(logOut, 3000);
          }
        }
      });
    }

    fetchData();

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [filter, toggleDisplayData]);

  const convertToDayJsObject = (date) => 
  {
    const convertedDate = date ? dayjs(date, "MM/DD/YYYY") : null;
    return convertedDate;
  }

  const handleDateChange = (newDate, type) => 
  {
    const date = newDate ? dayjs(newDate).format("MM/DD/YYYY") : newDate;
    type === "Start Date" ? setStartDate(date) :  setEndDate(date);
  }

  const handleFilter = () => 
  {
      setIsFiltered(true);
      setFilter(true);
      setToggleDisplayData(!toggleDisplayData);
  }

  const handleReset = () => 
  {
    setStartDate(null);
    setEndDate(null);
    setIsFilterable(false);
    setFilter(false);
    setIsFiltered(false);
    setToggleDisplayData(!toggleDisplayData);
  }

  const handleGlobalFilter = async (rowData) => 
  {

    setIsLoading(true);
    setShowAlert(false);
    setAlertMessage("");
    setAlertType("");

    await axios({
      method: "post",
      url: "/api/FilterTableData",
      data: {
        pathname: location?.pathname,
        table_data: rowData,
        filter_value: globalFilterValue
      },
      signal: abortController.signal
    })
    .then((response) => 
    {
      setIsLoading(false);
      const { status, data } = response;

      if (status === 200) 
      {
        tableRef?.current?.setFilteredData(data || []);
      }
      else 
      {
        setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
        setAlertType("error");
        setShowAlert(true);
      }
    })
    .catch((error) => 
    {
      console.log("Filter Table Data Api: ", error);
      setIsLoading(false);

      if (axios.isCancel(error) || error.code === "ERR_CANCELED") 
      {
        return;
      }

      const status = error?.response?.status;

      if (status === 403) 
      {
        triggerSessionExpire();
      }
      else 
      {
        setAlertMessage(
          status === 401
            ? "Unauthorized access. You do not have the required permissions to perform this action."
            : status === 429
            ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
            : "An error occurred while processing your request. Please try again later or contact the site administrator."
        );
        setAlertType("error");
        setShowAlert(true);

        if (status === 429) 
        {
          setTimeout(logOut, 3000);
        }
      }
    });
  }

  const handleLockStatusWrapper = (rowData) => 
  {
    console.log("hello ")
    setRowData(rowData);
    setShowLockStatusModal(true);
  }

  const handleLockStatus = useCallback(async () => 
  {
    setIsLoading(true);
    setShowAlert(false);
    setAlertMessage("");
    setAlertType("");

    await axios({
      method: "post",
      url: "/api/SetDocumentRequest",
      data: { 
        user_id: currentUser?.id,
        status: rowData?.status,
        document_request_id: rowData?.id,
        reviewed_comment: reviewedComment,
        additional_reviewed_comment: "",
        tranche_name: rowData["Tranche Name"] || ""
      },
      signal: abortController.signal
    })
    .then((response) => 
    {
      setIsLoading(false);
      const { status, data } = response;

      if (status === 200) 
      {
        setShowLockStatusModal(false);
        setAlertMessage(data?.message);
        setAlertType("success");
        setShowAlert(true);
        setToggleDisplayData(!toggleDisplayData);
        setReviewedComment(""); // Reset the comment field after submission
      }
      else 
      {
        setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
        setAlertType("error");
        setShowAlert(true);
      }
    })
    .catch((error) => 
    { 
      console.log("Set Bank Status Api: ", error);
      setIsLoading(false);

      if (axios.isCancel(error) || error.code === "ERR_CANCELED") 
      {
        return;
      }
      
      const status = error?.response?.status;

      if (status === 403) 
      {
        triggerSessionExpire();
      } 
      else 
      {
        setAlertMessage(
          status === 401
            ? "Unauthorized access. You do not have the required permissions to perform this action."
            : status === 429
            ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
            : "An error occurred while processing your request. Please try again later or contact the site administrator."
        );
        setAlertType("error");
        setShowAlert(true);
    
        if (status === 429) 
        {
          setTimeout(logOut, 3000);
        }
      }
    });

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [rowData?.Status, rowData?.id, rowData?.tranche_name, reviewedComment, toggleDisplayData]);

  const handleCloseModal = () => 
  {
    setShowLockStatusModal(false);
    setReviewedComment(""); // Reset the comment field when closing the modal
  }

  return (
    <div className = "content-container d-flex flex-column container mass-update position-relative">
        <Alert
            show = {showAlert}
            message = {alertMessage}
            type = {alertType}
            setShow = {setShowAlert}
        />

        <Backdrop
            sx = {{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open = {isLoading}
        >
            <Loader/>
        </Backdrop>

        <h4 className = "page-heading m-0">
            Requests
        </h4>
        
        <Grid container spacing = {1} justifyContent = "center" alignItems = "center">

            <Grid size = {{ xs: 2 }}>
                <LocalizationProvider dateAdapter = {AdapterDayjs}>
                    <DesktopDatePicker
                    inputFormat = "DD-MMM-YYYY"
                    label = "Start Date"
                    className = "date-picker w-100"
                    maxDate = {convertToDayJsObject(endDate)}
                    value = {convertToDayJsObject(startDate)}
                    onChange = {(newDate) => handleDateChange(newDate, "Start Date")}
                    onAccept = {(newDate) => handleDateChange(newDate, "Start Date")}
                    slotProps = {{
                        actionBar: {
                        actions: ["clear"]
                        },
                        textField: {
                        InputProps: {
                            size: "small",
                            disabled: true
                        }
                        }
                    }}
                    />
                </LocalizationProvider>
            </Grid>

            <Grid size = {{ xs: 2 }}>
                <LocalizationProvider dateAdapter = {AdapterDayjs}>
                    <DesktopDatePicker
                    inputFormat = "DD-MMM-YYYY"
                    label = "End Date"
                    className = "date-picker w-100"
                    minDate = {convertToDayJsObject(startDate)}
                    value = {convertToDayJsObject(endDate)}
                    onChange = {(newDate) => handleDateChange(newDate, "End Date")}
                    onAccept = {(newDate) => handleDateChange(newDate, "End Date")}
                    slotProps = {{
                        actionBar: {
                        actions: ["clear"]
                        },
                        textField: {
                        InputProps: {
                            size: "small",
                            disabled: true
                        }
                        }
                    }}
                    />
                </LocalizationProvider>
            </Grid>

            <Grid size = {{ xs: 2 }}>
                <ButtonGroup variant = "contained" disableElevation = {true}>
                    <Button type = "submit" className = "filter-button" color = "primary" disabled = {!isFilterable} onClick = {handleFilter}>Filter</Button>
                    <Button className = "reset-button" color = "info" disabled = {!isFiltered} startIcon = {<BiReset />} onClick = {handleReset}></Button>
                </ButtonGroup>
            </Grid>
        </Grid>

        <LocalizationProvider dateAdapter = {AdapterDayjs}>
            <MaterialReactDataTable
                ref = {tableRef}
                title = "Document Requests"
                isWritable = {isWritable}
                isLoading = {isLoading}
                isDataChanged = {isDataChanged}
                globalFilterValue = {globalFilterValue}
                tableData = {periodLockRequestsData}
                convertToDayJsObject = {convertToDayJsObject}
                setGlobalFilterValue = {setGlobalFilterValue}
                setIsDataChanged = {setIsDataChanged}
                handleGlobalFilter = {handleGlobalFilter}
                enableExpandAll = {false}
                showActions = {isDocumentTeamRole || isLCTeamRole}
                handleRecordStatusChange = {handleLockStatusWrapper}
                
            />
        </LocalizationProvider>

        <Modal show = {showLockStatusModal} onHide = {handleCloseModal} centered = {true}>
          <Modal.Header style={{padding: '7px 16px'}}closeButton = {true}>
            <Modal.Title>Are you sure?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <span>Do you really want to change the Lock status to {rowData?.Status}?</span>
            <Grid container spacing = {2} className = "align-items-start autocomplete mt-3">
              <Grid size = {{ xs: 12 }} className = "form-text-field">
                <TextField
                  label = "Reason for Status Change"
                  id = "reviewed_comment"
                  multiline
                  rows = {3}
                  value = {reviewedComment || ""}
                  onChange = {(event) => setReviewedComment(event.target.value)}
                  slotProps = {{ 
                    input: { readOnly: !isWritable }
                  }}
                />
              </Grid>
            </Grid>
          </Modal.Body>
          <Modal.Footer className = "gap-2">
            <Button variant = "outlined" size = "small" onClick = {handleCloseModal}>
              Cancel
            </Button>
            <Button
              variant = "contained"
              size = "small"
              onClick = {handleLockStatus}
              disabled = {!reviewedComment} // Disable Submit button if no reason is provided
            >
              Submit
            </Button>
          </Modal.Footer>
        </Modal>
    </div>
  );
}

export default withAuth(DocumentRequests)([
    roles[2], 
    roles[3], 
    roles[4], 
    roles[5], 
    roles[8],
    roles[10], 
    roles[11], 
    roles[13], 
    roles[14],
    roles[16], 
    roles[17]
]);